// GeoContentRefProvider.tsx — Geo suite, provider.
//
// Mounted by exactly the two surfaces that render stored session content: the
// product-version document and a collab message. Everywhere else the context
// keeps its null default.
//
// An incomplete pair (no session, or no record yet) provides **null** rather
// than a half-built ref: null means "no resolution channel here", which is
// exactly true, and it keeps a partially-loaded surface from issuing a request
// that could only 400.

'use client';

import { useMemo, type ReactElement, type ReactNode } from 'react';
import type { GeoContentRefKind } from '@cabal/shared/domain/geo/enums.js';
import type { GeoContentRef } from '@cabal/shared/domain/geo/types.js';
import { GeoContentRefContext } from './geo-content-ref-context';

export interface GeoContentRefProviderProps {
  kind: GeoContentRefKind;
  sessionId?: string | null;
  /** Product-version id or collab id, per `kind`. */
  recordId?: string | null;
  children: ReactNode;
}

export function GeoContentRefProvider({
  kind,
  sessionId,
  recordId,
  children,
}: GeoContentRefProviderProps): ReactElement {
  const value = useMemo<GeoContentRef | null>(
    () => (sessionId && recordId ? { kind, sessionId, recordId } : null),
    [kind, sessionId, recordId],
  );

  return (
    <GeoContentRefContext.Provider value={value}>{children}</GeoContentRefContext.Provider>
  );
}
